/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { Folder, Asset, Time, ActionRequest, FolderConfigs, TimeUtils, Ducks } from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

export enum ActionTypes {
  ADD = 'folderLastWatchedContent/ADD',
  UPDATE = 'folderLastWatchedContent/UPDATE',
}

interface AddMeta {
  folder: Folder;
  content: Asset;
}

interface UpdateMeta extends AddMeta {
  timestamp: Time;
}

type AddRequest = ActionRequest<ActionTypes.ADD, AddMeta>;
type UpdateRequest = ActionRequest<ActionTypes.UPDATE, UpdateMeta>;

type Actions = AddRequest | UpdateRequest;

const Actions = {
  add: (meta: AddMeta): AddRequest => ({ type: ActionTypes.ADD, meta }),
  update: (meta: UpdateMeta): UpdateRequest => ({ type: ActionTypes.UPDATE, meta }),
};

export const ActionCreators = {
  add: Actions.add,
};

interface FolderWatchedContent {
  content: Asset;
  timestamp: Time;
}

export interface ReducerState {
  [folderId: number]: FolderWatchedContent;
}

const initialState: ReducerState = {};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.UPDATE: {
      const { folder, content, timestamp } = action.meta;
      return {
        ...state,
        [folder.id]: {
          content,
          timestamp,
        },
      };
    }
    case ActionTypes.ADD:
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getFolderLastWatchedContent: (state: StoreState, folderId: number): Asset | undefined => {
    const folderConfigs: FolderConfigs | undefined = Ducks.Dms.Selectors.getFoldersConfig(state);
    const folderLastWatchedContent = state.folderLastWatchedContents[folderId];

    if (!folderLastWatchedContent || !folderConfigs) {
      return undefined;
    }

    const { lastWatchedContentCache } = folderConfigs;
    const { timestamp } = folderLastWatchedContent;

    const addTimestamp = TimeUtils.addDestructuredDuration(timestamp, lastWatchedContentCache);
    if (TimeUtils.isBeforeNow(addTimestamp)) {
      return state.folderLastWatchedContents[folderId].content;
    }

    return undefined;
  },
};

function* addRequestSaga(action: AddRequest): SagaIterator {
  const { folder, content } = action.meta;

  const folderLastWatchedContent: FolderWatchedContent | undefined = yield select(
    Selectors.getFolderLastWatchedContent,
    folder.id
  );

  if (!folderLastWatchedContent) {
    yield put(
      Actions.update({
        folder,
        content,
        timestamp: TimeUtils.now(),
      })
    );
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionTypes.ADD, addRequestSaga)]);
}
